import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiGrid, FiList, FiFileText, FiUsers, FiStar, FiDollarSign, FiTable, FiTrash2 } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { useVendedores } from '../../hooks/useVendedores';

const Vendedores = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('grid');
  
  // Usar el hook personalizado para vendedores
  const {
    vendedores,
    isLoading,
    error,
    searchQuery,
    searchVendedores,
    deleteVendedor,
    getVendedoresStats,
    clearError
  } = useVendedores();


  const handleAddNew = () => {
    navigate('/system/vendedores/nuevo');
  };

  const handleDeleteVendedor = useCallback(async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('¿Desactivar este vendedor? Su cuenta quedará inactiva pero sus datos se conservan.')) return;
    await deleteVendedor(id);
  }, [deleteVendedor]);

  const handleDownload = () => {
    console.log('Descargar lista de vendedores');
  };

  // Manejar búsqueda (actualiza query, la búsqueda real se hace con debounce)
  const handleSearchChange = (e) => {
    searchVendedores(e.target.value);
  };

  // Limpiar búsqueda
  const handleClearSearch = () => {
    searchVendedores('');
  };

  // Limpiar errores
  const handleClearError = () => {
    clearError();
  };

  const getThumbnailType = (rol, rendimiento, estado) => {
    // Todos los thumbnails en morado corporativo
    return 'blue-dark-gradient';
  };

  const getRolLabel = (rol) => {
    switch (rol) {
      case 'SALES': return 'Vendedor';
      case 'SALES_MANAGER': return 'Gerente de Ventas';
      case 'ADMIN': return 'Administrador';
      case 'MANAGER': return 'Gerente';
      case 'USER': return 'Usuario';
      default: return 'Usuario';
    }
  };

  const getRendimientoLabel = (rendimiento) => {
    switch (rendimiento) {
      case 'excelente': return 'Excelente';
      case 'muy_bueno': return 'Muy Bueno';
      case 'bueno': return 'Bueno';
      case 'regular': return 'Regular';
      case 'bajo': return 'Bajo';
      default: return 'Sin evaluar';
    }
  };

  const getRendimientoIcon = (rendimiento) => {
    const count = {
      'excelente': 5,
      'muy_bueno': 4,
      'bueno': 3,
      'regular': 2,
      'bajo': 1
    }[rendimiento] || 0;

    return '★'.repeat(count) + '☆'.repeat(5 - count);
  };

  if (isLoading) {
    return <LoadingGrid message="Cargando vendedores..." />;
  }

  return (
    <div className="vendedores-page">
      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <FiFileText className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
            <button 
              className="btn btn--ghost btn--sm" 
              onClick={handleClearError}
              style={{ marginLeft: '10px' }}
            >
              Reintentar
            </button>
          </div>
        </div>
      )}
      
      {/* Título */}
      <h1 className="page-title">VENDEDORES</h1>

      {/* Menú de acciones */}
      <div className="page-header-with-menu">
        <div className="action-menu">
          <button className="menu-item menu-item--first" onClick={handleAddNew}>
            + AGREGAR VENDEDOR
          </button>

          <div className="menu-item menu-item--search">
            <label className="search-label">BUSCAR:</label>
            <input
              type="text"
              className="search-input"
              placeholder="NOMBRE DEL VENDEDOR"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>

          <button className="menu-item menu-item--download" onClick={handleDownload}>
            DESCARGAR LISTA ↓
          </button>

          <div className="menu-item menu-item--views">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Vista de tarjetas"
            >
              <FiGrid />
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="Vista de lista"
            >
              <FiList />
            </button>
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Vista de tabla"
            >
              <FiTable />
            </button>
          </div>
        </div>
      </div>

      {/* Contenido */}
      {vendedores.length === 0 && !isLoading && !error ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FiUsers size={48} />
          </div>
          <h3 className="empty-state-title">
            {searchQuery ? 'No se encontraron vendedores' : 'No hay vendedores registrados'}
          </h3>
          <p className="empty-state-description">
            {searchQuery
              ? `No se encontraron vendedores que coincidan con "${searchQuery}"`
              : 'Comienza agregando tu primer vendedor al sistema'
            }
          </p>
          {searchQuery && (
            <p>
              <button
                className="btn btn--ghost"
                onClick={handleClearSearch}
              >
                Limpiar búsqueda
              </button>
            </p>
          )}
        </div>
      ) : viewMode === 'table' ? (
        /* Vista de Tabla */
        <div className="table-container">
          <div className="table-scroll">
            <table className="data-table data-table--vendedores">
              <thead>
                <tr>
                  <th>VENDEDOR</th>
                  <th>ROL</th>
                  <th>EMAIL</th>
                  <th>TELÉFONO</th>
                  <th className="text-center">CLIENTES</th>
                  <th className="text-center">VENTAS DEL MES</th>
                  <th className="text-center">RENDIMIENTO</th>
                  <th className="text-center">COMISIÓN</th>
                  <th className="text-center">ESTADO</th>
                  <th className="text-center">ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {vendedores.map((vendedor) => (
                  <tr
                    key={vendedor.id}
                    onClick={() => navigate(`/system/vendedores/${vendedor.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td><strong>{vendedor.nombre}</strong></td>
                    <td>{getRolLabel(vendedor.rol)}</td>
                    <td>{vendedor.email}</td>
                    <td>{vendedor.telefono || 'No registrado'}</td>
                    <td className="text-center">
                      <span className="badge badge-info">{vendedor.clientesAsignados}</span>
                    </td>
                    <td className="text-center">
                      <strong>${vendedor.ventasMes.toLocaleString()}</strong>
                    </td>
                    <td className="text-center">
                      <span className="performance-stars">{getRendimientoIcon(vendedor.rendimiento)}</span>
                    </td>
                    <td className="text-center">
                      {vendedor.comisionPorcentaje > 0
                        ? <span className="badge badge-warning">{vendedor.comisionPorcentaje}%</span>
                        : <span>—</span>}
                    </td>
                    <td className="text-center">
                      <span className={`badge ${vendedor.estado === 'activo' ? 'badge-activo' : 'badge-inactivo'}`}>
                        {vendedor.estado === 'activo' ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="text-center" onClick={e => e.stopPropagation()}>
                      <button
                        className="btn-table-delete"
                        onClick={e => handleDeleteVendedor(e, vendedor.id)}
                        title="Desactivar vendedor"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Vista Grid y Lista */
        <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'}>
          {vendedores.map((vendedor) => (
            <div
              key={vendedor.id}
              className={viewMode === 'grid' ? 'content-card' : 'content-card-list'}
              onClick={() => navigate(`/system/vendedores/${vendedor.id}`)}
              style={{ cursor: 'pointer' }}
            >
              {viewMode === 'grid' ? (
                <>
                  <div className={`content-thumbnail ${getThumbnailType(vendedor.rol, vendedor.rendimiento, vendedor.estado)}`}>
                    <span className="logo-text">
                      {vendedor.nombre.split(' ').map(word => word.charAt(0)).join('').substring(0, 3).toUpperCase()}
                    </span>

                    {/* Indicador de rendimiento */}
                    {vendedor.rendimiento === 'excelente' && (
                      <div className="status-badge status-badge--gold">
                        <FiStar size={12} />
                      </div>
                    )}

                    {/* Indicador de estado */}
                    {vendedor.estado === 'inactivo' && (
                      <div className="status-badge status-badge--gray">
                        Inactivo
                      </div>
                    )}
                  </div>
                  <div className="content-info">
                    <div className="content-title">{vendedor.nombre}</div>
                    <div className="content-subtitle">{getRolLabel(vendedor.rol)}</div>
                    <div className="content-details">
                      <div className="sales-stats">
                        <span className="stat-item">
                          <FiUsers size={12} /> {vendedor.clientesAsignados} clientes
                        </span>
                        <span className="stat-item">
                          <FiDollarSign size={12} /> ${vendedor.ventasMes.toLocaleString()} mes
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className={`content-thumbnail-small ${getThumbnailType(vendedor.rol, vendedor.rendimiento, vendedor.estado)}`}>
                    <span className="logo-text-small">
                      {vendedor.nombre.split(' ').map(word => word.charAt(0)).join('').substring(0, 2).toUpperCase()}
                    </span>
                  </div>
                  <div className="content-info-list">
                    <div className="content-title">{vendedor.nombre}</div>
                    <div className="content-subtitle">{getRolLabel(vendedor.rol)}</div>
                    <div className="content-meta">
                      <span className="meta-item">
                        Tel: {vendedor.telefono || 'No registrado'}
                      </span>
                      <span className="meta-item">
                        Email: {vendedor.email}
                      </span>
                      <span className="meta-item">
                        <FiUsers size={12} /> {vendedor.clientesAsignados} clientes
                      </span>
                      <span className="meta-item">
                        <FiDollarSign size={12} /> ${vendedor.ventasMes.toLocaleString()} este mes
                      </span>
                      {vendedor.comisionPorcentaje > 0 && (
                        <span className="meta-item info">
                          Comisión: {vendedor.comisionPorcentaje}%
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Vendedores;